import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CustomTable from "../../Component/Table";
import {
  activateDeactivateQuestion,
  getAllQuestionMaster,
  addQuestionOption,
} from "../../Services/form_question";
import DebouncedInput from "../../Component/DebouncedInput";
import { formatDate } from "../../utils/date";
import { Button, Flex, Modal, Tag } from "antd";
import AddEditModal from "./AddEditModel";
import PlanSwitchModal from "./PlanSwitchModal";
import ExpandableText from "../../Component/ExpandText";
import { getPlanAndOfferConfigAccess } from "../../Services/coupon";

const columns = (showEditModal, handleToggleActive) => [
  {
    title: "Question Name",
    dataIndex: "question",
    key: "question",
    render: (text) => <ExpandableText text={text} />,
  },
  {
    title: "Question Type",
    dataIndex: "question_type",
    key: "question_type",
  },
  {
    title: "Active",
    dataIndex: "is_active",
    key: "is_active",
    render: (text, record) => (
      <>
        <Tag color={record?.is_active ? "green" : "red"}>
          {record?.is_active ? "Yes" : "No"}
        </Tag>
      </>
    ),
  },
  {
    title: "Question Order",
    dataIndex: "question_order",
    key: "question_order",
  },
  {
    title: "Created At",
    dataIndex: "created_at",
    key: "created_at",
    render: (text, record) =>
      record?.created_at ? formatDate(record.created_at) : "-",
  },
  {
    title: "Updated At",
    dataIndex: "updated_at",
    key: "updated_at",
    render: (text, record) =>
      record?.updated_at ? formatDate(record.updated_at) : "-",
  },
  {
    title: "Actions",
    key: "actions",
    fixed: 'right',
    render: (_, record) => (
      <Flex gap="small">
        <Button type="link" onClick={() => showEditModal(record)}>
          Edit
        </Button>
        <Button
          variant="link"
          color={record?.is_active ? "danger" : "primary"}
          onClick={() => handleToggleActive(record)}
        >
          {" "}
          {record?.is_active ? "Deactivate" : "Activate"}
        </Button>
      </Flex>
    ),
  },
];

function FormQuestionMaster() {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditModalVisible, setIsAddEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [modelType, setModelType] = useState('add')
  const [isPlanModalVisible, setIsPlanModalVisible] = useState(false);

  const showAddModal = () => {
    setEditingRecord(null)
    setModelType('add')
    setIsAddEditModalVisible(true);
  }

  const showPlanModal = () => {
    setIsPlanModalVisible(true);
  }

  const showEditModal = (record) => {
    // setEditingRecord(record);
    setEditingRecord({
      ...record,
      question_options: record.options, 
    });
    setModelType('edit')
    setIsAddEditModalVisible(true);
  };

  
  useEffect(() => {
    setCurrentPage(1); 
  }, [searchTerm]);
  
  const skip = (currentPage - 1) * pageSize;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["form_question_master", currentPage, pageSize, searchTerm],
    queryFn: () =>
      getAllQuestionMaster({
        skip,
        limit: pageSize,
        name: searchTerm,
      }),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  // Plan Configuration is restricted to the admins listed in the
  // plan_and_offer_config_allowed_emails config value. Resolved server-side
  // from the caller's token, so the button only renders once access is
  // confirmed - everyone else never sees it.
  const { data: canSeePlanConfig } = useQuery({
    queryKey: ["plan-and-offer-config-access"],
    queryFn: getPlanAndOfferConfigAccess,
    refetchOnWindowFocus: false,
    retry: false,
  });



  const editMutation = useMutation({
    mutationFn: addQuestionOption,
    onSuccess: (data) => {
        if(data){
            refetch();
            setIsAddEditModalVisible(false);
        }
    },
  });



  const handleEditSubmit = (id, values) => {
    console.log("handle_submit==",id, values)
    editMutation.mutate({ id: id, ...values });
  };

  const activateMutation = useMutation({
    mutationFn: (data) =>
      activateDeactivateQuestion(data.id, data.is_active),
    onSuccess: () => {
      queryClient.invalidateQueries(["form_question_master"]); // Refetch user list after update
    },
  });

  const handleToggleActive = (form_mas) => {
    Modal.confirm({
      title: "Confirm",
      content: `Are you sure you want to ${
        form_mas.is_active ? "deactivate" : "activate"
      } this Form Question?`,
      okText: form_mas.is_active ? "Deactivate" : "Activate",
      cancelText: "Cancel",
      onOk: () => {
        activateMutation.mutate({
          id: form_mas.id,
          is_active: !form_mas.is_active, // Toggle Active Status
        });
      },
    });
  };

  return (
    <div>
      <h3>Form Question Master</h3>
      <Flex justify="space-between">
        <DebouncedInput
          placeholder="Search by Question..."
          value={searchTerm}
          onChange={setSearchTerm}
          delay={1000}
        />
        <Flex gap="15px">
          {canSeePlanConfig && (
            <Button type="primary" onClick={showPlanModal}>
              Plan Configuration
            </Button>
          )}
          <Button type="primary" onClick={showAddModal}>
            Add Form Question Master
          </Button>
        </Flex>
      </Flex>
      <br />
      <CustomTable
        columns={columns(showEditModal, handleToggleActive)}
        data={data?.data || []}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: data?.total_count || 0,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          position: ["bottomRight"],
        }}
        onTableChange={handleTableChange}
        maxHeight={"calc(90vh - 180px)"}
      />
      <AddEditModal
        key={editingRecord?.id || "new"}
        isModalVisible={isEditModalVisible}
        handleOk={handleEditSubmit}
        handleCancel={() => setIsAddEditModalVisible(false)}
        // loading={editMutation.isPending}
        initialValues={editingRecord}
        modelType={modelType}
      />
      <PlanSwitchModal
        key='plan_switch'
        isModalVisible={isPlanModalVisible}
        data={data?.config_data || []}
        handleCancel={() => setIsPlanModalVisible(false)}
      />
      
    </div>
  );
}

export default FormQuestionMaster;
